import axios, { type AxiosError } from 'axios';
import { TokenRefreshAuthError, TokenRefreshNetworkError } from '../errors';
import type { AuthStoreAdapter } from '../types/auth';
import type { TokenRefreshConfig, RetryStrategy } from '../types/config';
import { ErrorCategory } from '../types/retry';
import { classifyError, getRetryStrategyForError } from '../classification';
import { sleep } from '../utils';

/**
 * Dependencies for token refresh function.
 */
export interface RefreshAccessTokenDependencies {
  /** Base URL for API requests */
  baseURL: string;

  /** Auth store for getting/setting tokens */
  authStore: AuthStoreAdapter;

  /** Token refresh configuration */
  config: TokenRefreshConfig;

  /** Function to retry all queued requests (for network error UI) */
  retryQueuedRequests: () => Promise<void>;

  /** Callback to show network error UI */
  onNetworkError: (message: string, retryQueue: () => Promise<void>, isColdStart: boolean) => void;

  /** Client type identifier for version tracking */
  clientType: 'portal' | 'mobile';

  /** Application version for version tracking */
  appVersion: string;
}

/**
 * Refresh access token with configurable retry logic.
 *
 * Uses retry strategies from config based on error classification.
 * Performs automatic retries with exponential backoff for infrastructure failures.
 *
 * Error Handling:
 * - Throws TokenRefreshAuthError for genuine auth failures (401, 400) → logout
 * - Throws TokenRefreshNetworkError for infrastructure failures → show retry UI
 *
 * Retry Strategy:
 * - Classifies each error (ColdStart, Timeout, NetworkError, etc.)
 * - Uses appropriate retry strategy from config
 * - Shows UI after first retry fails (if configured)
 * - Invokes telemetry hooks for monitoring
 *
 * @param deps Dependencies (baseURL, authStore, config, callbacks)
 * @returns Promise resolving to the new access token
 * @throws TokenRefreshAuthError for auth failures (logout)
 * @throws TokenRefreshNetworkError for network failures (retry UI)
 *
 * @example
 * ```typescript
 * try {
 *   const newToken = await refreshAccessToken(deps);
 *   // Use new token
 * } catch (error) {
 *   if (error instanceof TokenRefreshAuthError) {
 *     // Logout user
 *   } else if (error instanceof TokenRefreshNetworkError) {
 *     // Show retry UI
 *   }
 * }
 * ```
 */
export async function refreshAccessToken(
  deps: RefreshAccessTokenDependencies
): Promise<string> {
  const { baseURL, authStore, config, retryQueuedRequests, onNetworkError, clientType, appVersion } = deps;

  const refreshToken = authStore.getRefreshToken();

  if (!refreshToken) {
    console.error('❌ Token refresh failed: No refresh token available - will logout', {
      statusCode: 401,
      reason: 'no_refresh_token',
      willLogout: true,
    });
    throw new TokenRefreshAuthError("No refresh token available", 401);
  }

  let lastError: AxiosError | null = null;
  let retryStrategy: RetryStrategy | undefined = undefined;
  let errorCategory: ErrorCategory = ErrorCategory.Unknown; // Default to Unknown
  let hasShownNetworkError = false; // Track if we've shown the network error UI

  // Initial attempt + retries (max 10 attempts safety limit)
  for (let attempt = 1; attempt <= 10; attempt++) {
    try {
      console.log(`🔄 Token refresh attempt ${attempt}`);

      const response = await axios.post(
        `${baseURL}/api/v1/auth/refresh`,
        { refreshToken },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Client-Version": appVersion,
            "X-Client-Type": clientType,
          },
          timeout: config.timeouts.authEndpoint,
        }
      );

      const { identityToken: newToken, refreshToken: newRefreshToken } =
        response.data;

      if (!newToken || !newRefreshToken) {
        throw new TokenRefreshAuthError(
          "Invalid token refresh response",
          400
        );
      }

      // Success! Update auth store
      authStore.setTokens(newToken, newRefreshToken);
      console.log(`✅ Token refresh succeeded on attempt ${attempt}`);

      // Telemetry: retry success (if this wasn't the first attempt)
      if (
        attempt > 1 &&
        config.telemetry?.enabled &&
        config.telemetry.onRetrySuccess &&
        retryStrategy
      ) {
        config.telemetry.onRetrySuccess({
          errorCategory,
          successfulAttempt: attempt,
          totalAttempts: attempt,
          label: retryStrategy.telemetryLabel,
          timestamp: new Date().toISOString(),
        });
      }

      return newToken;
    } catch (error) {
      lastError = error as AxiosError;

      // Classify error and get retry strategy on first error
      if (!retryStrategy) {
        errorCategory = classifyError(
          lastError,
          config.customErrorClassifier
        );
        retryStrategy = getRetryStrategyForError(lastError, config);
      }

      // Check if retry is enabled for this error type
      if (!retryStrategy.enabled) {
        const statusCode = lastError.response?.status || 500;
        console.error(
          `❌ Token refresh failed (${errorCategory}): retry disabled - HTTP ${statusCode}`,
          {
            statusCode,
            errorCode: lastError.code,
            errorMessage: lastError.message,
            endpoint: '/api/v1/auth/refresh',
            willLogout: retryStrategy.shouldLogout,
          }
        );

        if (retryStrategy.shouldLogout) {
          throw new TokenRefreshAuthError(
            retryStrategy.userMessage,
            statusCode
          );
        } else {
          throw new TokenRefreshNetworkError(
            retryStrategy.userMessage,
            lastError
          );
        }
      }

      // Check if we've exhausted retries
      if (attempt >= retryStrategy.maxAttempts) {
        const statusCode = lastError.response?.status || 500;
        console.error(
          `❌ Token refresh failed after ${attempt} attempts (${errorCategory}) - HTTP ${statusCode}`,
          {
            statusCode,
            errorCode: lastError.code,
            errorMessage: lastError.message,
            endpoint: '/api/v1/auth/refresh',
            attemptsMade: attempt,
            maxAttempts: retryStrategy.maxAttempts,
            willLogout: retryStrategy.shouldLogout,
          }
        );

        // Telemetry: retry exhausted
        if (config.telemetry?.enabled && config.telemetry.onRetryExhausted) {
          config.telemetry.onRetryExhausted({
            errorCategory,
            attemptsMade: attempt,
            error: lastError.message,
            label: retryStrategy.telemetryLabel,
            timestamp: new Date().toISOString(),
          });
        }

        // Format user message with attempt count
        const userMessage = retryStrategy.userMessage.replace(
          "{{attempts}}",
          attempt.toString()
        );

        if (retryStrategy.shouldLogout) {
          throw new TokenRefreshAuthError(
            userMessage,
            statusCode
          );
        } else {
          const isColdStart = errorCategory === ErrorCategory.ColdStart;
          throw new TokenRefreshNetworkError(userMessage, lastError, hasShownNetworkError, isColdStart);
        }
      }

      // Retry with delay
      const delay =
        retryStrategy.delays[attempt - 1] ||
        retryStrategy.delays[retryStrategy.delays.length - 1] ||
        1000;
      console.log(
        `🥶 Token refresh error (${errorCategory}, attempt ${attempt}/${retryStrategy.maxAttempts}), retrying in ${delay}ms...`,
        lastError.code || lastError.message
      );

      // Telemetry: retry attempt
      if (config.telemetry?.enabled && config.telemetry.onRetryAttempt) {
        config.telemetry.onRetryAttempt({
          errorCategory,
          attemptNumber: attempt,
          maxAttempts: retryStrategy.maxAttempts,
          delayMs: delay,
          label: retryStrategy.telemetryLabel,
          timestamp: new Date().toISOString(),
        });
      }

      await sleep(delay);

      // Show network error UI after first retry fails (if configured)
      // This happens AFTER the first delay, meaning attempt 2 is about to start
      if (
        !hasShownNetworkError &&
        retryStrategy.shouldShowRetryUI &&
        retryStrategy.showMessageAfterFirstRetry &&
        attempt === 1
      ) {
        hasShownNetworkError = true;
        const userMessage = retryStrategy.userMessage.replace(
          "{{attempts}}",
          (attempt + 1).toString() // Will show "Attempt 2 of 5" after first retry
        );

        // Trigger network error UI callback immediately after first retry
        // This allows UI to show dialog while retry loop continues in background
        if (onNetworkError) {
          const isColdStart = errorCategory === ErrorCategory.ColdStart;
          onNetworkError(userMessage, retryQueuedRequests, isColdStart);
        }
      }
    }
  }

  // Should never reach here (max 10 attempts safety), but TypeScript needs this
  const isColdStart = errorCategory === ErrorCategory.ColdStart;
  throw new TokenRefreshNetworkError(
    "Token refresh failed after maximum attempts",
    lastError,
    hasShownNetworkError,
    isColdStart
  );
}
